import { Node, mergeAttributes } from '@tiptap/core';

/**
 * The ONE structured element beyond prose (D-cms-pages-core): a call-to-action. It is
 * a block node whose visible text is the CTA label (inline content) plus a link (href)
 * and a tone. It is a CUSTOM TipTap node so it ROUND-TRIPS in the canonical ProseMirror
 * JSON (a `cta` node with attrs href / tone and inline text content). The rich-text CMS
 * is prose-with-one-accent: this is the only embed, NOT a dynamic block (those are the
 * page-schema editor's domain).
 *
 * The tone is carried as `data-tone` (NOT a `style` attribute, which the browser would
 * read as CSS); the href as a real anchor href. Authoring restricts the node set to the
 * configured extensions, so an unsupported node (a table, a column) cannot be created.
 */
export interface CtaAttributes {
  href: string;
  tone: 'primary' | 'secondary' | 'subtle';
}

export const Cta = Node.create({
  name: 'cta',
  group: 'block',
  content: 'inline*',
  defining: true,

  addAttributes() {
    return {
      href: {
        default: '',
        parseHTML: (el) => el.getAttribute('href') ?? '',
        renderHTML: (attrs) => ({ href: attrs.href }),
      },
      tone: {
        default: 'primary',
        parseHTML: (el) => el.getAttribute('data-tone') ?? 'primary',
        renderHTML: (attrs) => ({ 'data-tone': attrs.tone }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'a[data-cta]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'a',
      mergeAttributes(HTMLAttributes, { 'data-cta': '', class: 'cms-cta' }),
      0,
    ];
  },
});
